/**
 * Mise en page d'un média (image aujourd'hui, vidéo demain) portée par son
 * attribut `style` : une largeur et un alignement, rien d'autre.
 *
 * C'est le SEUL format qui traverse toute la chaîne des contenus riches :
 * l'assainisseur de l'API ne garde de `style` que `width` (en `px` ou `%`) et
 * `text-align`, et retire les classes et les attributs `data-*`. L'alignement
 * est donc stocké dans `text-align` — sans effet propre sur une image, il sert
 * de porteur — et c'est à l'affichage (`SafeHtml`) et dans l'éditeur qu'il est
 * traduit en marges.
 *
 * Logique pure, partagée par l'éditeur (lecture / écriture du HTML) et par
 * `SafeHtml` (réécriture stricte à l'affichage).
 */

export type MediaAlign = 'left' | 'center' | 'right';

export interface MediaLayout {
  /** Largeur CSS, « 50% » ou « 320px ». `null` : taille d'origine. */
  width: string | null;
  /** `null` : pas d'alignement explicite (à gauche). */
  align: MediaAlign | null;
}

/** Même forme que celle acceptée par l'API : un nombre suivi de `px` ou `%`. */
const WIDTH_PATTERN = /^\d+(?:\.\d+)?(?:px|%)$/;
const ALIGNS: readonly string[] = ['left', 'center', 'right'];

/** Attribut posé à l'affichage pour que la feuille de style puisse aligner le média. */
export const MEDIA_ALIGN_ATTRIBUTE = 'data-media-align';

export function isMediaWidth(value: string | null | undefined): value is string {
  return !!value && WIDTH_PATTERN.test(value);
}

/**
 * Lit la mise en page dans la valeur d'un attribut `style`. Toute déclaration
 * autre que `width` et `text-align`, ou de valeur inattendue, est ignorée : ce
 * qui ressort peut être réécrit tel quel sans risque.
 */
export function parseMediaLayout(style: string | null | undefined): MediaLayout {
  const layout: MediaLayout = { width: null, align: null };
  for (const declaration of (style ?? '').split(';')) {
    const separator = declaration.indexOf(':');
    if (separator === -1) continue;
    const property = declaration.slice(0, separator).trim().toLowerCase();
    const value = declaration.slice(separator + 1).trim().toLowerCase();
    if (property === 'width' && WIDTH_PATTERN.test(value)) layout.width = value;
    if (property === 'text-align' && ALIGNS.includes(value)) layout.align = value as MediaAlign;
  }
  return layout;
}

/** Valeur de `style` à STOCKER (largeur et alignement) ; `null` s'il n'y a rien à écrire. */
export function serializeMediaLayout(layout: MediaLayout): string | null {
  const declarations = [
    isMediaWidth(layout.width) ? `width: ${layout.width}` : null,
    layout.align ? `text-align: ${layout.align}` : null,
  ].filter(Boolean);
  return declarations.length > 0 ? declarations.join('; ') : null;
}
